/**
 * PostHog error tracking and analytics client.
 *
 * EU cloud region — events ingested via eu.i.posthog.com. The region MUST
 * match the region the VITE_POSTHOG_KEY belongs to: an EU key only works
 * against the EU host, and an old US key against this host silently drops
 * every event. So when migrating region, rotate each environment's
 * *_VITE_POSTHOG_KEY to the new region in the same release as this change.
 *
 * VITE_POSTHOG_HOST / VITE_POSTHOG_UI_HOST can override the host (e.g. a local
 * developer pointing at a scratch project); the deploy workflows don't set
 * them, so a one-off region rollback is a direct edit here, not a secret flip.
 *
 * Only initialises when VITE_POSTHOG_KEY is set, so dev machines without
 * the key configured are silently unaffected.
 */
import posthog from 'posthog-js'
import { APP_BUILD } from './build'
import { getAppEnv } from './env'

const API_HOST =
  import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'
const UI_HOST =
  import.meta.env.VITE_POSTHOG_UI_HOST || 'https://eu.posthog.com'

/**
 * Initialise PostHog. Call once before the React tree mounts (in main.tsx).
 * No-op when VITE_POSTHOG_KEY is not set.
 */
export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY

  if (!key) {
    return
  }

  posthog.init(key, {
    api_host: API_HOST,
    ui_host: UI_HOST,

    // Only create person profiles for identified users — avoids charging
    // quota on anonymous sessions that never log in.
    person_profiles: 'identified_only',

    // Disable automatic pageview capture — we send pageviews manually so
    // they include the tenant slug context set via identifyUser().
    capture_pageview: false,

    // Disable autocapture — opt-in to explicit capture calls only.
    // Re-enable once you know which interactions you want tracked.
    autocapture: false,

    // Error tracking — capture unhandled JS exceptions automatically.
    capture_exceptions: true,

    // Tag every event with the build that sent it (`app_build`), so sessions
    // on an out-of-date build can be counted. Set at send time, not with
    // register(): registered properties are saved in the browser, so an old
    // build starting after a newer one would report the newer build's id.
    before_send: (event) => {
      if (event) {
        event.properties.app_build = APP_BUILD
      }
      return event
    },

    // Opt-out on local only. Dev, staging and pre-production all report into
    // the same EU PostHog project (257349); production has its own (168957).
    // Every event is tagged with `environment` so those can be told apart —
    // any insight on the shared project must filter on it, or dev traffic
    // inflates staging numbers.
    //
    // Dev is included deliberately: without it, instrumentation cannot be
    // observed until it reaches staging. To turn dev back off, unset the
    // DEV_VITE_POSTHOG_KEY secret — with no key init() never runs.
    loaded(ph) {
      const environment = getAppEnv()

      if (environment === 'local') {
        ph.opt_out_capturing()
        return
      }

      ph.register({ environment })
    },

    // Session recording off by default; only record when an exception fires.
    session_recording: {
      // Mask every input value by default; maskInputFn then opts specific
      // fields back in.
      maskAllInputs: true,
      // Opt-in unmask: only an input explicitly tagged data-replay-unmask="true"
      // records its real value (e.g. the product search box, so replays show
      // what engineers actually typed). Everything else — job reference, the
      // reason box, the phone field, and any input added later — stays masked.
      // Default-deny: a new field is hidden unless someone deliberately tags it.
      // Optional-chained throughout so a missing element can never throw and
      // break the recording.
      maskInputFn: (text, element) => {
        if (element?.getAttribute?.('data-replay-unmask') === 'true') {
          return text
        }
        return '*'.repeat(text.length)
      },
    },
    enable_recording_console_log: false,
  })
}

/**
 * Identify the authenticated user so all subsequent events are linked to
 * them in PostHog. Call after a successful /api/users/me response.
 *
 * @param userId  Backend UUID (stable across sessions).
 * @param traits  Additional properties stored on the PostHog person record.
 * @returns True when all identify/register/group calls succeeded. Callers that
 *   dedupe re-identification should cache their key only on true, so a failed
 *   attempt is retried on the next call rather than marked as synced.
 */
export function identifyUser(
  userId: string,
  traits: {
    email: string
    firstName: string
    lastName: string
    role: string
    tenantId: string | null
    tenantSlug: string | null
    tenantName: string | null
    isPlatformAdmin: boolean
  },
): boolean {
  // Runs on the login path, so it must never be able to throw into the app.
  // Analytics is allowed to be missing; it is not allowed to break sign-in.
  try {
    posthog.identify(userId, {
      email: traits.email,
      first_name: traits.firstName,
      last_name: traits.lastName,
      role: traits.role,
      tenant_id: traits.tenantId,
      tenant_slug: traits.tenantSlug,
      tenant_name: traits.tenantName,
      is_platform_admin: traits.isPlatformAdmin,
    })

    // Carry tenant on every event as an EVENT property, not only as a person
    // property. Person properties are latest-value: if a user moves tenant,
    // historical analysis would silently rewrite itself.
    posthog.register({
      tenant_id: traits.tenantId,
      tenant_slug: traits.tenantSlug,
    })

    // Group by tenant so tenant-level stats aggregate correctly in PostHog.
    // Applies to every event captured after this call.
    if (traits.tenantId && traits.tenantSlug) {
      posthog.group('tenant', traits.tenantId, {
        slug: traits.tenantSlug,
        name: traits.tenantName ?? traits.tenantSlug,
      })
    }
    return true
  } catch {
    // Never surface an analytics failure to the user.
    return false
  }
}

/**
 * Reset PostHog identity — call on logout so the next anonymous session
 * is not merged with the previous user.
 */
export function resetPostHog(): void {
  // On the logout path — same rule as identifyUser: analytics must not be
  // able to block a sign-out.
  try {
    posthog.reset()
  } catch {
    // Never surface an analytics failure to the user.
  }
}

export { posthog }
